import type { Session } from '@supabase/supabase-js';
import { getSupabaseClient } from '@/core/supabase/client.js';
import { logAuthEvent } from '@/core/debug/authDebugLog.js';
import type {
  AdminSetUserPasswordInput,
  AuthStateSubscriber,
  ChangePasswordInput,
  LoginInput,
} from '@/features/authentication/domain/repositories/IAuthRepository.js';
import type { AuthSessionEntity } from '@/features/authentication/domain/entities/AuthSessionEntity.js';

function summarizeSession(session: Session | null): Record<string, unknown> | null {
  if (!session) return null;
  return {
    uid: session.user?.id,
    expires_at: session.expires_at,
    expires_in: session.expires_in,
    secondsToExpiry: session.expires_at
      ? session.expires_at - Math.floor(Date.now() / 1000)
      : null,
  };
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1] ?? '';
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
}

async function extractEdgeFunctionError(error: unknown, fallback: string): Promise<string> {
  try {
    const context = (error as { context?: Response } | null)?.context;
    if (context && typeof context.clone === 'function') {
      const response = context.clone();
      const body = await response.json() as { error?: unknown; message?: unknown };
      if (body?.error) return String(body.error);
      if (body?.message) return String(body.message);
    }
  } catch {
    // Fall through to the SDK error message.
  }
  return (error as { message?: string } | null)?.message ?? fallback;
}

function mapSession(session: Session | null): AuthSessionEntity | null {
  if (!session?.user) return null;
  const jwt = decodeJwtPayload(session.access_token);
  const rbac = (jwt?.rbac ?? {}) as Record<string, unknown>;
  return {
    uid: session.user.id,
    email: session.user.email ?? "",
    displayName:
      (session.user.user_metadata?.display_name as string | undefined) ?? null,
    photoURL:
      (session.user.user_metadata?.avatar_url as string | undefined) ?? null,
    isSuperAdmin: !!(rbac?.is_super_admin || session.user.app_metadata?.is_super_admin),
  };
}

export class AuthRemoteDataSource {
  async login(input: LoginInput): Promise<AuthSessionEntity> {
    logAuthEvent("login:start", { email: input.email });
    const { data, error } = await getSupabaseClient().auth.signInWithPassword({
      email: input.email,
      password: input.password,
    });
    if (error || !data.session) {
      logAuthEvent("login:error", { message: error?.message, status: (error as { status?: number } | null)?.status });
      throw new Error(error?.message ?? "Đăng nhập thất bại.");
    }
    logAuthEvent("login:success", summarizeSession(data.session));
    return mapSession(data.session) as AuthSessionEntity;
  }

  async changePassword(input: ChangePasswordInput): Promise<void> {
    if (input.newPassword.length < 8) {
      throw new Error('Mật khẩu mới phải có ít nhất 8 ký tự.');
    }
    if (input.currentPassword === input.newPassword) {
      throw new Error('Mật khẩu mới phải khác mật khẩu hiện tại.');
    }

    const { data: sessionData, error: sessionError } = await getSupabaseClient().auth.getSession();
    const email = sessionData.session?.user?.email;
    if (sessionError || !email) {
      throw new Error('Phiên đăng nhập không hợp lệ. Vui lòng đăng nhập lại.');
    }

    // Re-authenticate with the current password before changing credentials.
    const { error: reauthError } = await getSupabaseClient().auth.signInWithPassword({
      email,
      password: input.currentPassword,
    });
    if (reauthError) {
      throw new Error('Mật khẩu hiện tại không đúng.');
    }

    const { error } = await getSupabaseClient().auth.updateUser({
      password: input.newPassword,
    });
    if (error) throw new Error(error.message);
  }

  async adminSetUserPassword(input: AdminSetUserPasswordInput): Promise<void> {
    const { data, error } = await getSupabaseClient().functions.invoke(
      'admin-set-user-password',
      {
        body: {
          target_user_id: input.targetUserId,
          org_id: input.orgId,
          temporary_password: input.temporaryPassword,
        },
      },
    );
    if (error) {
      throw new Error(await extractEdgeFunctionError(error, 'Đặt lại mật khẩu thất bại.'));
    }
    if (data?.error) throw new Error(String(data.error));
  }

  async logout(): Promise<void> {
    logAuthEvent("logout:call", { stack: new Error().stack });
    const { error } = await getSupabaseClient().auth.signOut();
    if (error) {
      logAuthEvent("logout:error", { message: error.message });
      throw new Error(error.message);
    }
    logAuthEvent("logout:success");
  }

  async getSession(): Promise<AuthSessionEntity | null> {
    const { data, error } = await getSupabaseClient().auth.getSession();
    if (error) {
      logAuthEvent("getSession:error", { message: error.message });
      throw new Error(error.message);
    }
    logAuthEvent("getSession:result", summarizeSession(data.session));
    return mapSession(data.session);
  }

  async refreshSession(): Promise<AuthSessionEntity | null> {
    logAuthEvent("refreshSession:call");
    const { data, error } = await getSupabaseClient().auth.refreshSession();
    if (error) {
      logAuthEvent("refreshSession:error", { message: error.message, status: (error as { status?: number }).status });
      throw new Error(error.message);
    }
    logAuthEvent("refreshSession:result", summarizeSession(data.session));
    return mapSession(data.session);
  }

  observeAuthState(subscriber: AuthStateSubscriber): () => void {
    const { data } = getSupabaseClient().auth.onAuthStateChange(
      (event, session) => {
        logAuthEvent(`onAuthStateChange:${event}`, {
          ...summarizeSession(session),
          visibility: typeof document !== "undefined" ? document.visibilityState : undefined,
          online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
        });
        subscriber(mapSession(session));
      },
    );
    return () => data.subscription.unsubscribe();
  }
}
